import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Input,
  Button,
  Breadcrumb,
  Modal,
  message,
  Form,
  Select,
  Checkbox,
  Space,
  Row,
  Col,
  Grid,
  Card,
  Typography,
  Avatar,
  Tag,
  Tooltip,
  Statistic,
  Empty,
  Divider,
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import {
  DeleteOutlined,
  EditOutlined,
  RightOutlined,
  UserOutlined,
  TeamOutlined,
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  ClockCircleOutlined,
  CrownOutlined,
  CustomerServiceOutlined,
  DollarOutlined,
  ShopOutlined,
  SettingOutlined,
  KeyOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { UserService, rolesService } from '../../common/services';
import Can from '../../components/can';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text } = Typography;

// Mapping des icônes par nom
const ICON_MAP = {
  crown: <CrownOutlined />,
  'customer-service': <CustomerServiceOutlined />,
  dollar: <DollarOutlined />,
  shop: <ShopOutlined />,
  setting: <SettingOutlined />,
  lock: <LockOutlined />,
};

// Configuration par défaut des rôles (fallback)
const DEFAULT_ROLE_CONFIG = {
  directeur: { color: 'purple', label: 'Directeur', icon: <CrownOutlined />, bgColor: '#f9f0ff' },
  support: { color: 'blue', label: 'Support', icon: <CustomerServiceOutlined />, bgColor: '#e6f7ff' },
  comptable: { color: 'green', label: 'Comptable', icon: <DollarOutlined />, bgColor: '#f6ffed' },
  commercial: { color: 'orange', label: 'Commercial', icon: <ShopOutlined />, bgColor: '#fff7e6' },
  admin: { color: 'default', label: 'Admin', icon: <SettingOutlined />, bgColor: '#fafafa' },
};

// Couleurs pour les avatars
const avatarColors = [
  '#f56a00', '#7265e6', '#ffbf00', '#00a2ae', '#87d068',
  '#108ee9', '#eb2f96', '#722ed1', '#13c2c2', '#fa541c'
];

const getAvatarColor = (name) => {
  if (!name) return avatarColors[0];
  const charCode = name.charCodeAt(0);
  return avatarColors[charCode % avatarColors.length];
};

const ListeDesAdministeurs = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal édition
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  // Permissions & presets
  const [permList, setPermList] = useState([]);
  const [groupOrder, setGroupOrder] = useState([]);
  const [groupLabels, setGroupLabels] = useState({});

  // Rôles depuis la BDD
  const [dbRoles, setDbRoles] = useState([]);
  const [rolesMap, setRolesMap] = useState({});

  // Breakpoints
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const controlSize = isMobile ? 'middle' : 'large';

  // Chargement
  const loadData = () => {
    setLoading(true);
    Promise.all([
      UserService.listeDesAdministrateurs(),
      rolesService.getPermissions().then(r => r?.data || r),
      rolesService.getForSelect().then(r => r?.data || []),
    ])
      .then(([adminsData, permsData, rolesData]) => {
        setAdmins(Array.isArray(adminsData) ? adminsData : []);

        const list = Array.isArray(permsData?.list)
          ? permsData.list
          : (permsData?.all || []).map(k => ({ key: k, label: k, group: null, groupLabel: null }));

        setPermList(list);
        setGroupLabels(permsData?.groupLabels || {});
        setGroupOrder([...new Set(list.map(i => i.group).filter(Boolean))]);

        // Stocker les rôles de la BDD
        setDbRoles(rolesData || []);
        const rMap = {};
        (rolesData || []).forEach(r => {
          rMap[r.name] = r;
        });
        setRolesMap(rMap);
      })
      .catch((err) => {
        console.error('Erreur chargement:', err);
        message.error('Erreur de chargement');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let local = phone.replace('+225', '').trim();
    return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  // Stats
  const stats = useMemo(() => {
    const byRole = {};
    admins.forEach(a => {
      const role = a.role || 'admin';
      byRole[role] = (byRole[role] || 0) + 1;
    });
    return {
      total: admins.length,
      byRole,
    };
  }, [admins]);

  // Recherche
  const filteredAdmins = admins.filter((admin) => {
    if (!admin) return false;
    const { phone = '', firstName = '', lastName = '', id = '', role = '' } = admin;
    const search = (searchTerm || '').toLowerCase();
    return (
      phone.toLowerCase().includes(search) ||
      firstName.toLowerCase().includes(search) ||
      lastName.toLowerCase().includes(search) ||
      role.toLowerCase().includes(search) ||
      id.toLowerCase().includes(search)
    );
  });

  // Suppression
  const handleDelete = (administrateur) => {
    const { _id, firstName, lastName, phone } = administrateur;

    Modal.confirm({
      title: 'Confirmer la suppression',
      icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div style={{ marginTop: 16 }}>
          <Text>Êtes-vous sûr de vouloir supprimer cet administrateur ?</Text>
          <Card size="small" style={{ marginTop: 12, backgroundColor: '#fafafa' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar style={{ backgroundColor: getAvatarColor(firstName) }} size={40}>
                {(firstName?.[0] || '').toUpperCase()}{(lastName?.[0] || '').toUpperCase()}
              </Avatar>
              <div>
                <Text strong>{firstName} {lastName}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <PhoneOutlined style={{ marginRight: 4 }} />
                  {formatPhoneNumber(phone)}
                </Text>
              </div>
            </div>
          </Card>
        </div>
      ),
      okText: 'Supprimer',
      cancelText: 'Annuler',
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        try {
          await UserService.deleteAdministrateur(_id);
          message.success('Administrateur supprimé avec succès !');
          setAdmins(prev => prev.filter(p => p._id !== _id));
        } catch (error) {
          console.error('Erreur:', error);
          message.error('Une erreur est survenue.');
        }
      }
    });
  };

  // Réinitialiser le mot de passe
  const handleResetPassword = (administrateur) => {
    const { _id, firstName, lastName, phone } = administrateur;

    Modal.confirm({
      title: 'Réinitialiser le mot de passe',
      icon: <KeyOutlined style={{ color: '#faad14' }} />,
      content: (
        <div style={{ marginTop: 16 }}>
          <Text>Voulez-vous réinitialiser le mot de passe de cet administrateur à <strong>123456</strong> ?</Text>
          <Card size="small" style={{ marginTop: 12, backgroundColor: '#fffbe6', border: '1px solid #ffe58f' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar style={{ backgroundColor: getAvatarColor(firstName) }} size={40}>
                {(firstName?.[0] || '').toUpperCase()}{(lastName?.[0] || '').toUpperCase()}
              </Avatar>
              <div>
                <Text strong>{firstName} {lastName}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <PhoneOutlined style={{ marginRight: 4 }} />
                  {formatPhoneNumber(phone)}
                </Text>
              </div>
            </div>
          </Card>
        </div>
      ),
      okText: 'Réinitialiser',
      cancelText: 'Annuler',
      okButtonProps: { style: { backgroundColor: '#faad14', borderColor: '#faad14' } },
      centered: true,
      onOk: async () => {
        try {
          const response = await UserService.resetPassword(_id);
          if (response?.success) {
            message.success(response.message || 'Mot de passe réinitialisé à 123456');
          } else {
            message.error(response?.message || 'Erreur lors de la réinitialisation');
          }
        } catch (error) {
          console.error('Erreur:', error);
          message.error('Une erreur est survenue.');
        }
      }
    });
  };

  // Ouvrir modal édition
  const openEdit = (record) => {
    setEditingUser(record);
    const roleData = rolesMap[record.role];
    form.setFieldsValue({
      firstName: record.firstName,
      lastName: record.lastName,
      phone: (record.phone || '').replace('+225', ''),
      role: record.role || 'admin',
      permissions: record.permissions || roleData?.permissions || [],
      email: record.email || '',
      residence: record.residence || '',
      country: record.country || 'civ',
    });
    setEditOpen(true);
  };

  const onRoleChange = (r) => {
    const roleData = rolesMap[r];
    const preset = roleData?.permissions || [];
    form.setFieldsValue({ role: r, permissions: preset });
  };

  const onEditSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = { ...values, phone: `+225${values.phone}` };

      const body = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        phone: payload.phone,
        email: payload.email,
        residence: payload.residence,
        country: payload.country,
        role: payload.role,
        permissions: payload.permissions,
      };

      await UserService.miseAjourRoleEtPermissions(editingUser._id, body);
      message.success('Administrateur mis à jour !');
      setEditOpen(false);
      setAdmins(prev => prev.map(u => (u._id === editingUser._id ? { ...u, ...body } : u)));
    } catch (e) {
      if (e?.errorFields) return;
      console.error(e);
      message.error('Échec de la mise à jour');
    }
  };

  const columns = [
    {
      title: 'Administrateur',
      key: 'admin',
      render: (_, record) => {
        const fullName = `${record.firstName || ''} ${record.lastName || ''}`.trim();
        const initials = `${(record.firstName?.[0] || '').toUpperCase()}${(record.lastName?.[0] || '').toUpperCase()}`;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar
              style={{ backgroundColor: getAvatarColor(record.firstName), flexShrink: 0 }}
              size={isMobile ? 36 : 44}
            >
              {initials || <UserOutlined />}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              <Text strong style={{ display: 'block', fontSize: isMobile ? 13 : 14 }}>
                {fullName || 'Sans nom'}
              </Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <PhoneOutlined style={{ marginRight: 4 }} />
                {formatPhoneNumber(record.phone)}
              </Text>
              {record.email && !isMobile && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                  <MailOutlined style={{ marginRight: 4 }} />
                  {record.email}
                </Text>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      width: isMobile ? 100 : 140,
      render: (role) => {
        const dbRole = rolesMap[role];
        const defaultConfig = DEFAULT_ROLE_CONFIG[role] || { color: 'default', label: role, icon: <UserOutlined /> };
        const config = dbRole
          ? { color: dbRole.color, label: dbRole.label, icon: ICON_MAP[dbRole.icon] || <SettingOutlined /> }
          : defaultConfig;
        return (
          <Tag
            color={config.color}
            icon={config.icon}
            style={{
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: isMobile ? 11 : 12,
            }}
          >
            {isMobile ? '' : config.label}
          </Tag>
        );
      },
      filters: dbRoles.length > 0
        ? dbRoles.map((r) => ({ text: r.label, value: r.name }))
        : Object.entries(DEFAULT_ROLE_CONFIG).map(([value, config]) => ({ text: config.label, value })),
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Date d\'ajout',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (date) => (
        <Tooltip title={date ? dayjs(date).format('dddd DD MMMM YYYY à HH:mm') : ''}>
          <Space size={4}>
            <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
            <span style={{ fontSize: 13 }}>
              {date ? dayjs(date).format('DD/MM/YYYY') : '-'}
            </span>
          </Space>
        </Tooltip>
      ),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      responsive: ['md'],
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Can perm="users:update">
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEdit(record)}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          </Can>
          <Can perm="users:update">
            <Tooltip title="Réinitialiser mot de passe">
              <Button
                type="text"
                icon={<KeyOutlined />}
                onClick={() => handleResetPassword(record)}
                style={{ color: '#faad14' }}
              />
            </Tooltip>
          </Can>
          <Can perm="users:delete">
            <Tooltip title="Supprimer">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          </Can>
        </Space>
      ),
    },
  ];

  // Stats cards config
  const statsCards = [
    {
      title: 'Total',
      value: stats.total,
      icon: <TeamOutlined />,
      color: '#722ed1',
      bgGradient: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
    },
    {
      title: 'Directeurs',
      value: stats.byRole.directeur || 0,
      icon: <CrownOutlined />,
      color: '#722ed1',
      bgGradient: 'linear-gradient(135deg, #f9f0ff 0%, #d3adf7 100%)',
    },
    {
      title: 'Support',
      value: stats.byRole.support || 0,
      icon: <CustomerServiceOutlined />,
      color: '#1890ff',
      bgGradient: 'linear-gradient(135deg, #e6f7ff 0%, #91d5ff 100%)',
    },
    {
      title: 'Commerciaux',
      value: stats.byRole.commercial || 0,
      icon: <ShopOutlined />,
      color: '#fa8c16',
      bgGradient: 'linear-gradient(135deg, #fff7e6 0%, #ffd591 100%)',
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        separator={<RightOutlined />}
        style={{ marginBottom: 20 }}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: <span>Administration</span> },
          { title: <span style={{ fontWeight: 600 }}>Administrateurs</span> },
        ]}
      />

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <SafetyCertificateOutlined style={{ color: '#722ed1' }} />
            Gestion des Administrateurs
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Gérez les accès et permissions des utilisateurs du backoffice
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={loadData}
            disabled={isLoading}
          >
            {!isMobile && 'Actualiser'}
          </Button>
          <Can perm="users:create">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/dashboard/ajout-administrateur')}
            >
              {isMobile ? '' : 'Nouvel admin'}
            </Button>
          </Can>
        </Space>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statsCards.map((card, index) => (
          <Col xs={12} sm={12} md={6} key={index}>
            <Card
              hoverable
              style={{
                background: card.bgGradient,
                border: 'none',
                borderRadius: 16,
                height: '100%',
              }}
              bodyStyle={{ padding: isMobile ? 16 : 20 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: isMobile ? 11 : 12, display: 'block', marginBottom: 4 }}
                  >
                    {card.title}
                  </Text>
                  <Text
                    style={{
                      fontSize: isMobile ? 24 : 32,
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.2,
                    }}
                  >
                    {card.value}
                  </Text>
                </div>
                <div
                  style={{
                    width: isMobile ? 36 : 44,
                    height: isMobile ? 36 : 44,
                    borderRadius: 10,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? 18 : 20,
                    color: card.color,
                  }}
                >
                  {card.icon}
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Recherche */}
      <Card
        style={{ marginBottom: 24, borderRadius: 12 }}
        bodyStyle={{ padding: isMobile ? 16 : 20 }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={12} lg={10}>
            <Input
              size={controlSize}
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par nom, prénom, rôle ou numéro..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {filteredAdmins.length} administrateur{filteredAdmins.length > 1 ? 's' : ''} trouvé{filteredAdmins.length > 1 ? 's' : ''}
            </Text>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
        <Table
          rowKey={(p) => p._id?.toString()}
          columns={columns}
          dataSource={filteredAdmins}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 'max-content' }}
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: !isMobile,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {`${range[0]}-${range[1]} sur ${total}`}
              </Text>
            ),
            style: { padding: '12px 16px', margin: 0 },
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <Text type="secondary">
                    Aucun administrateur trouvé
                  </Text>
                }
              />
            ),
          }}
          rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
        />
      </Card>

      {/* Modal édition */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EditOutlined style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>Modifier l'administrateur</div>
              <div style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 400 }}>
                {editingUser?.firstName} {editingUser?.lastName}
              </div>
            </div>
          </div>
        }
        open={editOpen}
        okText="Enregistrer"
        onOk={onEditSubmit}
        onCancel={() => setEditOpen(false)}
        destroyOnClose
        centered
        width={screens.xl ? 820 : screens.lg ? 720 : screens.md ? 600 : '95%'}
        styles={{ body: { padding: isMobile ? 16 : 24, maxHeight: '70vh', overflowY: 'auto' } }}
      >
        <Form form={form} layout="vertical" size={isMobile ? 'middle' : 'large'}>
          <Divider orientation="left" orientationMargin={0}>
            <Text strong style={{ fontSize: 14 }}>Informations personnelles</Text>
          </Divider>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item name="firstName" label="Nom" rules={[{ required: true, message: 'Requis' }]}>
                <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item name="lastName" label="Prénom" rules={[{ required: true, message: 'Requis' }]}>
                <Input prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="phone"
                label="Téléphone"
                rules={[
                  { required: true, message: 'Requis' },
                  { pattern: /^[0-9]{10}$/, message: '10 chiffres' },
                ]}
              >
                <Input addonBefore="+225" maxLength={10} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item name="email" label="Email">
                <Input prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item name="residence" label="Résidence">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={8}>
              <Form.Item name="role" label="Rôle" rules={[{ required: true }]}>
                <Select onChange={onRoleChange}>
                  {dbRoles.map(r => (
                    <Select.Option key={r.name} value={r.name}>
                      <Space>
                        <span style={{ color: r.color || '#595959' }}>
                          {ICON_MAP[r.icon] || <SettingOutlined />}
                        </span>
                        <span>{r.label}</span>
                      </Space>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" orientationMargin={0}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text strong style={{ fontSize: 14 }}>Permissions</Text>
              <Button
                size="small"
                type="link"
                onClick={() => {
                  const r = form.getFieldValue('role');
                  const roleData = rolesMap[r];
                  form.setFieldsValue({ permissions: roleData?.permissions || [] });
                }}
              >
                Réinitialiser
              </Button>
            </div>
          </Divider>

          <Form.Item name="permissions" noStyle>
            <Checkbox.Group style={{ width: '100%' }}>
              {groupOrder.map((g) => {
                const items = permList.filter(p => p.group === g);
                if (!items.length) return null;
                return (
                  <Card
                    key={g}
                    size="small"
                    title={<Text strong style={{ fontSize: 13 }}>{groupLabels?.[g] || g}</Text>}
                    style={{ marginBottom: 12, borderRadius: 8 }}
                    bodyStyle={{ padding: 12 }}
                  >
                    <Row gutter={[8, 8]}>
                      {items.map(p => (
                        <Col key={p.key} xs={24} sm={12} md={8}>
                          <Checkbox value={p.key} style={{ fontSize: 13 }}>{p.label}</Checkbox>
                        </Col>
                      ))}
                    </Row>
                  </Card>
                );
              })}

              {permList.some(p => !p.group) && (
                <Card
                  size="small"
                  title={<Text strong style={{ fontSize: 13 }}>Autres</Text>}
                  style={{ borderRadius: 8 }}
                  bodyStyle={{ padding: 12 }}
                >
                  <Row gutter={[8, 8]}>
                    {permList.filter(p => !p.group).map(p => (
                      <Col key={p.key} xs={24} sm={12} md={8}>
                        <Checkbox value={p.key} style={{ fontSize: 13 }}>{p.label}</Checkbox>
                      </Col>
                    ))}
                  </Row>
                </Card>
              )}
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* Styles */}
      <style>{`
        .table-row-light {
          background-color: #ffffff;
        }
        .table-row-dark {
          background-color: #fafafa;
        }
        .table-row-light:hover td,
        .table-row-dark:hover td {
          background-color: #f9f0ff !important;
        }
        .ant-table-thead > tr > th {
          background-color: #fafafa !important;
          font-weight: 600 !important;
          color: #262626 !important;
        }
        .ant-pagination {
          background-color: #fafafa;
          border-top: 1px solid #f0f0f0;
        }
      `}</style>
    </div>
  );
};

export default ListeDesAdministeurs;
